/**
 * A small language model running in the operator's own browser.
 *
 * <h3>What this is for</h3>
 * Trying interpreters against each other. The planner's counting is the same
 * whoever read the sentence, so the only variable worth testing is the reading
 * itself — and the cheapest way to test a reading is to swap the thing doing it
 * and send the same sentence again.
 *
 * <h3>What a model here is trusted with</h3>
 * Saying what the sentence MEANT: an intent, and the words it thinks name the
 * campaign. Nothing else. The campaign is resolved by SQL on the server, the
 * file is parsed and counted there, do-not-contact is checked there. So the
 * worst a 1B model can do is aim at the wrong campaign — in a plan that then
 * says so, in counts it did not produce.
 *
 * <h3>Why it is loaded from a CDN and not bundled</h3>
 * The library is large and the weights are ~1GB; nobody who never opens this
 * should pay for either. The import is deliberately ignored by the bundler so
 * the portal's own build stays the size it was.
 */

export interface BrowserModelHandle {
  id: string;
  ask(message: string, hasFile: boolean, headers: string[]): Promise<SuppliedReading>;
  /** Free the GPU memory. Two of these live at once will exhaust it on a laptop. */
  unload(): Promise<void>;
}

/** How far a download has got, for a bar rather than a sentence. */
export interface LoadProgress {
  /** 0..1 where the library reports it; null while it is still working that out. */
  fraction: number | null;
  text: string;
}

/**
 * Which models this browser has already downloaded.
 *
 * <h3>Why it is remembered rather than asked</h3>
 * The weights live in the browser's own cache, so a second load is fast and
 * costs no network — but nothing in the page can tell you that until it tries.
 * Remembering which ones got as far as running means the picker can say "1.2 GB,
 * first time" for one model and "already here" for another, which is the
 * difference between an informed choice and a surprise.
 *
 * <p>It can be wrong in one direction only: a cleared browser cache leaves the
 * note behind, and the download simply happens again. It never claims a model
 * is missing when it is present.
 */
const DOWNLOADED_KEY = 'operator.models.downloaded';

export const downloadedModels = (): string[] => {
  try { return JSON.parse(localStorage.getItem(DOWNLOADED_KEY) || '[]'); } catch { return []; }
};

const rememberDownloaded = (id: string) => {
  try {
    // Array rather than a spread Set: this project's TS target predates
    // downlevelIteration, and a build error here is a silly way to lose a load.
    const all = downloadedModels();
    if (all.indexOf(id) < 0) all.push(id);
    localStorage.setItem(DOWNLOADED_KEY, JSON.stringify(all));
  } catch { /* a browser that refuses storage just loses the hint */ }
};

/** How the answer was got out of the model, weakest last. */
export type ReadMode = 'tools' | 'grammar' | 'unconstrained';

export interface SuppliedReading {
  intent: string;
  campaignHint: string | null;
  /** What the model actually returned, for the trial notes. Never repaired. */
  raw: string;
  ms: number;
  /** Set when the model named a campaign that does not appear in the message. */
  discardedHint?: string;

  /**
   * How it was asked.
   *
   * <p>Worth showing, because a wrong reading means three different things.
   * Under `tools` the model chose a named function and filled its argument;
   * under `grammar` it filled a JSON shape; under `unconstrained` it was free to
   * return prose and the parse did the rest. Blaming the model for the third is
   * the confusion a trial exists to avoid.
   */
  mode: ReadMode;
}

/** WebGPU is the whole requirement; there is no WASM fallback worth offering here. */
export const browserModelsPossible = (): boolean =>
  typeof navigator !== 'undefined' && 'gpu' in navigator;

const CDN = 'https://esm.run/@mlc-ai/web-llm';

let libPromise: Promise<any> | null = null;
const loadLib = () => {
  // eslint-disable-next-line
  if (!libPromise) libPromise = import(/* webpackIgnore: true */ CDN);
  return libPromise;
};

/**
 * The small models this browser could actually run, read from the library's own
 * catalogue rather than hardcoded.
 *
 * A pinned list goes stale silently — the id changes upstream, the load fails
 * with something unhelpful, and it looks like the feature is broken rather than
 * the list. Anything above ~3B is filtered out: it is not that it cannot run,
 * it is that a business laptop with thirty tabs open should not be asked to.
 */
export async function availableBrowserModels(): Promise<string[]> {
  const webllm: any = await loadLib();
  const all: any[] = webllm?.prebuiltAppConfig?.model_list ?? [];
  return all
    .map((m) => String(m.model_id ?? ''))
    // Small instruct models, PLUS the tool-calling ones — which are all 7-8B.
    // The size filter exists so a laptop is not asked to run something it
    // cannot; a model that can call a tool earns the exception, and the picker
    // says what it costs.
    .filter((id) =>
      supportsTools(id) ||
      (/-(0\.5B|1B|1\.5B|1\.7B|2b|2B|3B)-/i.test(id) && /instruct|it-/i.test(id)))
    .sort();
}

/**
 * The models that can actually call a tool.
 *
 * <h3>Pinned, and read out of the library rather than assumed</h3>
 * WebLLM gates `ChatCompletionRequest.tools` to an allowlist —
 * `functionCallingModelIds` in web-llm 0.2.85 — and every one of them is a 7-8B
 * Hermes. There is no small tool-calling model in the catalogue, which is the
 * honest answer to "can a browser model call our tools": yes, at about 4-5GB
 * and a GPU with room for it.
 *
 * <p>This list is advisory only: it decides what the picker LABELS, never what
 * is attempted. The runtime refuses an unsupported model itself, and that
 * refusal is caught and falls back, so a newer library that widens the
 * allowlist starts working here without this list being edited.
 */
export const TOOL_CALLING_MODELS = [
  'Hermes-2-Pro-Llama-3-8B-q4f16_1-MLC',
  'Hermes-2-Pro-Llama-3-8B-q4f32_1-MLC',
  'Hermes-2-Pro-Mistral-7B-q4f16_1-MLC',
  'Hermes-3-Llama-3.1-8B-q4f16_1-MLC',
  'Hermes-3-Llama-3.1-8B-q4f32_1-MLC',
];

export const supportsTools = (id: string) => TOOL_CALLING_MODELS.indexOf(id) >= 0;

/**
 * The four things the planner can be asked for, as callable functions.
 *
 * <h3>Why this is better than the JSON shape it falls back to</h3>
 * A tool call names what the model decided and puts the campaign in a NAMED
 * argument, so "which campaign" stops being a free string the model may fill
 * with whatever it last read. It is the same contract the MCP tools will expose
 * to Claude and ChatGPT — which is the point of trying it here first.
 *
 * <p>No system prompt goes with these: WebLLM refuses a custom system message
 * when tools are in play (CustomSystemPromptError), so the descriptions ARE the
 * instruction. They are written to be read by a model, hence the plain
 * "asking how to do a thing is not asking for it to be done".
 */
const TOOLS = [
  {
    type: 'function',
    function: {
      name: 'add_file_to_campaign',
      description:
        'Add the people in the attached spreadsheet to an existing WhatsApp campaign. ' +
        'Only when the user is asking for it to be DONE — asking how to do it is not asking for it.',
      parameters: {
        type: 'object',
        properties: {
          campaign_name: { type: 'string', description: 'The campaign named in the message, copied word for word.' },
        },
        required: ['campaign_name'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'campaign_performance',
      description: 'How one campaign did — how many were sent, delivered, read, replied.',
      parameters: {
        type: 'object',
        properties: {
          campaign_name: { type: 'string', description: 'The campaign named in the message, copied word for word.' },
        },
        required: ['campaign_name'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'list_campaigns',
      description: 'List the WhatsApp campaigns that exist and what state each is in.',
      parameters: { type: 'object', properties: {} },
    },
  },
  {
    type: 'function',
    function: {
      name: 'explain_product',
      description:
        'Explain what something in this product means or how to do it — standing campaigns, ' +
        'opt-out, template variables, calling hours. Use this for any question about how it works.',
      parameters: {
        type: 'object',
        properties: { topic: { type: 'string', description: 'What they asked about.' } },
        required: ['topic'],
      },
    },
  },
];

const TOOL_TO_INTENT: Record<string, string> = {
  add_file_to_campaign: 'ADD_FILE_TO_CAMPAIGN',
  campaign_performance: 'CAMPAIGN_PERFORMANCE',
  list_campaigns: 'LIST_CAMPAIGNS',
  explain_product: 'EXPLAIN',
};

/**
 * The shape the answer must take, as a STRING.
 *
 * <h3>Why a string, and why a schema at all</h3>
 * WebLLM's grammar matcher takes the schema as serialized JSON. Asking for
 * `{ type: "json_object" }` and no schema hands `undefined` to the C++ side,
 * which fails with "Cannot pass non-string to std::string" — an error that
 * reads like a broken model and is really a missing argument. Seen on
 * Qwen2.5-Coder-0.5B.
 *
 * <p>campaignHint is a plain string rather than string-or-null: union types are
 * not uniformly supported by the grammar backends, and the failure mode is this
 * same unhelpful error. An empty string means "no campaign named", and it is
 * turned back into null on the way out.
 */
const SCHEMA = JSON.stringify({
  type: 'object',
  properties: {
    tool: {
      type: 'string',
      enum: ['add_file_to_campaign', 'campaign_performance', 'list_campaigns', 'explain_product', 'none'],
    },
    campaign_name: { type: 'string' },
  },
  // tool only. Requiring campaign_name MAKES the model produce one — on the
  // message "Hi", a 0.5B model answered with a campaign called "no", because
  // the grammar left it no way to say nothing. A schema that forces a field
  // forces an invention.
  required: ['tool'],
});

/**
 * The same four operations, described for a model that has no tools API.
 *
 * <h3>Why the vocabulary is identical to TOOLS above</h3>
 * WebLLM's tools API is Hermes-only, and every Hermes is 7-8B — but the CONTRACT
 * does not need the API. A named operation with a named argument, constrained to
 * a grammar, is tool calling in every sense that matters here; the library's
 * parsing is the only part a small model misses out on.
 *
 * <p>Keeping both paths on one vocabulary is what makes the trial worth running:
 * Hermes answering through `tools` and Qwen answering through a grammar produce
 * the same shape, so the difference in the result is the MODEL rather than the
 * question.
 */
const SYSTEM = [
  'You read one message from somebody running WhatsApp campaigns and answer with JSON only.',
  'Answer exactly: {"tool": "...", "campaign_name": "..."}',
  'add_file_to_campaign: they want the people in the attached file added to a campaign.',
  'campaign_performance: they are asking how a campaign did — sent, delivered, read, replied.',
  'list_campaigns: they are asking which campaigns exist.',
  'explain_product: they are asking what something means or how to do it. Asking HOW to add',
  'people is explain_product, not add_file_to_campaign — asking how to do a thing is not',
  'asking for it to be done.',
  'none: anything else, including sending, pausing or cancelling, and any greeting.',
  'campaign_name is the words naming the campaign, copied from the message. Never invent one.',
  'If no campaign is named, leave campaign_name out entirely.',
  'Examples. "Hi" -> {"tool":"none"}.',
  '"add this file to the Diwali offer campaign" -> {"tool":"add_file_to_campaign","campaign_name":"Diwali offer"}.',
  '"how did the salon promo do" -> {"tool":"campaign_performance","campaign_name":"salon promo"}.',
  '"what does standing mean" -> {"tool":"explain_product"}.',
].join(' ');

export async function loadBrowserModel(
  modelId: string,
  onProgress: (p: LoadProgress) => void,
): Promise<BrowserModelHandle> {
  const webllm: any = await loadLib();
  const engine = await webllm.CreateMLCEngine(modelId, {
    initProgressCallback: (p: any) => onProgress({
      fraction: typeof p?.progress === 'number' ? p.progress : null,
      text: p?.text ?? 'loading…',
    }),
  });
  rememberDownloaded(modelId);

  return {
    id: modelId,
    async unload() {
      try { await engine.unload?.(); } catch { /* already gone */ }
    },
    async ask(message, hasFile, headers) {
      const started = performance.now();
      const user = [
        `Instruction: ${message}`,
        `A file is attached: ${hasFile ? 'yes' : 'no'}`,
        headers.length ? `The file's columns are: ${headers.join(', ')}` : '',
      ].filter(Boolean).join('\n');

      const messages = [{ role: 'system', content: SYSTEM }, { role: 'user', content: user }];

      // ── 1. tool calling, where the model has it ──────────────────────────
      // The strongest form: the model picks a named function and fills a named
      // argument, which is the same contract the MCP tools will hand Claude and
      // ChatGPT. No system message — WebLLM refuses one when tools are in play.
      if (supportsTools(modelId)) {
        try {
          const res = await engine.chat.completions.create({
            messages: [{ role: 'user', content: user }],
            tools: TOOLS,
            tool_choice: 'auto',
            temperature: 0,
          });
          const call = res?.choices?.[0]?.message?.tool_calls?.[0];
          const raw = call ? JSON.stringify(call) : (res?.choices?.[0]?.message?.content ?? '');
          const ms = Math.round(performance.now() - started);
          if (call) {
            let args: any = {};
            try { args = JSON.parse(call.function?.arguments ?? '{}'); } catch { /* reported raw */ }
            const hint = typeof args.campaign_name === 'string' ? args.campaign_name.trim() : '';
            return finish(
              TOOL_TO_INTENT[call.function?.name] ?? 'UNRECOGNISED',
              hint || null, raw, ms, 'tools', message,
            );
          }
          // Chose to answer in prose instead of calling anything. That is a
          // real answer to "did it understand", and UNRECOGNISED is the honest
          // reading of it rather than a guess at what it meant.
          return finish('UNRECOGNISED', null, raw, ms, 'tools', message);
        } catch (e) {
          // An allowlist that moved, or a model that cannot really do it. Fall
          // through rather than end the turn.
        }
      }

      // ── 2. a grammar ─────────────────────────────────────────────────────
      // At this size unconstrained JSON is a coin toss, and the interesting
      // question is whether the model READ the sentence, not whether it can
      // close a brace.
      let mode: ReadMode = 'grammar';
      let res: any;
      try {
        res = await engine.chat.completions.create({
          messages,
          response_format: { type: 'json_object', schema: SCHEMA },
          temperature: 0,
          max_tokens: 200,
        });
      } catch (e: any) {
        // ── 3. nothing at all ──────────────────────────────────────────────
        // Not every model in the catalogue has a working grammar backend, and a
        // harness that dies on one cannot tell you the model was the problem.
        mode = 'unconstrained';
        res = await engine.chat.completions.create({
          messages, temperature: 0, max_tokens: 200,
        });
      }
      const raw = res?.choices?.[0]?.message?.content ?? '';
      const ms = Math.round(performance.now() - started);

      // Parsed leniently, REPAIRED never. A harness that quietly fixes what the
      // model said is a harness that flatters it — and the whole point is to
      // find out which models need fixing.
      let intent = 'UNRECOGNISED';
      let hint: string | null = null;
      try {
        const json = JSON.parse(raw.slice(raw.indexOf('{'), raw.lastIndexOf('}') + 1));
        // The same mapping the tools path uses, so a model answering through a
        // grammar and one answering through the tools API are held to one
        // vocabulary and one meaning.
        if (typeof json.tool === 'string') intent = TOOL_TO_INTENT[json.tool.trim()] ?? 'UNRECOGNISED';
        if (typeof json.campaign_name === 'string' && json.campaign_name.trim()) {
          hint = json.campaign_name.trim();
        }
      } catch {
        /* left as UNRECOGNISED — the server will say it did not understand, which is true */
      }
      return finish(intent, hint, raw, ms, mode, message);
    },
  };
}

/**
 * The one exit, so every path is held to the same rule.
 *
 * <p>A campaign name is DEFINED as words copied out of the message. One that is
 * not in the message was invented, and is dropped — enforcing the contract, not
 * repairing the answer. It is reported either way, because a harness that
 * silently cleans up after a model cannot tell you which models need cleaning up
 * after. This caught a 0.5B model answering the greeting "Hi" with a campaign
 * called "no".
 */
function finish(
  intent: string, hint: string | null, raw: string, ms: number,
  mode: ReadMode, message: string,
): SuppliedReading {
  let campaignHint = hint;
  let discardedHint: string | undefined;
  if (campaignHint) {
    const flat = message.toLowerCase().replace(/\s+/g, ' ');
    if (!flat.includes(campaignHint.toLowerCase().replace(/\s+/g, ' '))) {
      discardedHint = campaignHint;
      campaignHint = null;
    }
  }
  return { intent, campaignHint, raw, ms, mode, discardedHint };
}
